/**
 * Página do caso, lida como parte do dossiê do cliente.
 *
 * Topo: nome do caso, número CNJ (clique copia), vara, cliente e responsável;
 * etiqueta de status (clique altera, com confirmação) e interruptor
 * Em andamento/Arquivado. Abaixo: prazos e tarefas, documentos, financeiro.
 * Descrição, valores, juiz e etiquetas ficam em "Mais detalhes", recolhido.
 * O número interno do escritório (ex. 3/2026) não aparece.
 */
import { useCallback, useEffect, useState, type ReactNode } from 'react'
import { Link, useParams } from 'react-router-dom'
import { ArrowLeft, ChevronDown, Edit, FileText, Scale } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible'
import { Skeleton } from '@/components/ui/skeleton'
import { DetailField } from '@/components/common/DetailSection'
import { CaseArchiveSwitch, CaseStatusControl } from '@/components/cases/CaseStatusControl'
import { Case, CaseAreaLabels } from '../types/case'
import { caseService } from '../services/case-service'
import { CaseDocuments } from '../components/CaseDocuments'
import { CaseContingencyFees } from '../components/CaseContingencyFees'
import { FinanceSummaryCard } from '../components/finance-v2/FinanceSummaryCard'
import { useDateTimeFormat } from '../hooks/useDateTimeFormat'
import { TaskQuickCreateButton, TaskContext } from '../components/tasks/TaskQuickCreateButton'
import { TaskListByEntity } from '../components/tasks/TaskListByEntity'
import { ContextualBreadcrumb } from '../components/common/ContextualBreadcrumb'
import { ConvertCaseToLegalProcessModal } from '../components/cases/ConvertCaseToLegalProcessModal'
import { CopyableProcessNumber } from '../components/ui/CopyableProcessNumber'
import { getErrorMessage } from '../utils/error.utils'
import { cn } from '@/utils/cn'

function Folded({ title, children, defaultOpen = false }: { title: string; children: ReactNode; defaultOpen?: boolean }) {
  const [open, setOpen] = useState(defaultOpen)
  return (
    <Collapsible open={open} onOpenChange={setOpen} className="rounded-xl border bg-card">
      <CollapsibleTrigger className="flex w-full items-center justify-between gap-2 px-4 py-3 text-left font-semibold">
        {title}
        <ChevronDown className={cn('size-4 text-muted-foreground transition-transform', open && 'rotate-180')} aria-hidden="true" />
      </CollapsibleTrigger>
      <CollapsibleContent className="border-t px-4 py-4">{children}</CollapsibleContent>
    </Collapsible>
  )
}

export function CaseDetailsPage() {
  const { id } = useParams<{ id: string }>()
  const { formatDateOnly } = useDateTimeFormat()
  const [case_, setCase] = useState<Case | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [taskRefreshTrigger, setTaskRefreshTrigger] = useState(0)
  const [showConvertToLegalProcessModal, setShowConvertToLegalProcessModal] = useState(false)

  const loadCase = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      if (!id) throw new Error('ID do caso não encontrado')
      setCase(await caseService.getCaseById(id))
    } catch (err: unknown) {
      setError(getErrorMessage(err, 'Erro ao carregar caso'))
    } finally {
      setLoading(false)
    }
  }, [id])

  useEffect(() => {
    if (id) loadCase()
  }, [id, loadCase])

  // A API devolve o caso sem o cliente expandido: mantém o que já estava na tela.
  const applyUpdate = (updated: Case) => setCase((prev) => (prev ? { ...prev, ...updated, client: updated.client ?? prev.client } : updated))

  const formatCurrency = (value?: number) =>
    value ? new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value) : null

  if (loading) {
    return (
      <div className="mx-auto flex w-full max-w-5xl flex-col gap-4 p-4 sm:p-6" aria-busy="true">
        <Skeleton className="h-5 w-64" />
        <Skeleton className="h-32" />
        <Skeleton className="h-48" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex flex-col gap-4 p-4 sm:p-6">
        <div role="alert" className="rounded-lg border border-destructive/30 bg-destructive/5 p-4">
          <h3 className="font-medium text-destructive">Erro ao carregar caso</h3>
          <p className="mt-1 text-sm text-muted-foreground">{error}</p>
          <div className="mt-3 flex flex-wrap gap-2">
            <Button onClick={() => loadCase()}>Tentar novamente</Button>
            <Button variant="outline" asChild><Link to="/clients">Voltar para clientes</Link></Button>
          </div>
        </div>
      </div>
    )
  }

  if (!case_) {
    return (
      <div className="flex flex-col items-center gap-2 p-4 py-12 text-center sm:p-6">
        <FileText className="size-10 text-muted-foreground" aria-hidden="true" />
        <h3 className="text-sm font-medium">Caso não encontrado</h3>
        <p className="text-sm text-muted-foreground">O caso solicitado não pôde ser encontrado.</p>
        <Button asChild className="mt-4"><Link to="/clients">Voltar para clientes</Link></Button>
      </div>
    )
  }

  const clientName = case_.client?.name || 'Cliente não informado'
  const clientHref = case_.clientId ? `/clients/${case_.clientId}` : undefined
  const canConvert = case_.status !== 'CONVERTED_TO_LEGAL' && !case_.courtNumber && !case_.courtName
  const estimatedValue = formatCurrency(case_.estimatedValue)
  const hasMoreDetails = !!(case_.description || case_.judgeName || estimatedValue || case_.successFeePercentage || case_.startDate || case_.tags?.length)

  return (
    <div className="mx-auto flex w-full max-w-5xl flex-col gap-6 p-4 sm:p-6">
      <ContextualBreadcrumb
        items={[
          { label: 'Clientes', href: '/clients' },
          { label: clientName, href: clientHref },
          { label: case_.title }
        ]}
      />

      <Card className="gap-0">
        <CardContent className="flex flex-col gap-4">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <div className="min-w-0">
              <h1 className="flex items-center gap-2 break-words text-2xl font-semibold">
                <Scale className="size-6 shrink-0 text-muted-foreground" aria-hidden="true" />
                <span className="min-w-0">{case_.title}</span>
              </h1>
              {case_.courtNumber && (
                <CopyableProcessNumber number={case_.courtNumber} className="mt-2 text-lg text-foreground" />
              )}
            </div>
            <div className="flex shrink-0 flex-wrap items-center gap-3 sm:flex-col sm:items-end sm:gap-2">
              <CaseStatusControl caseId={case_.id} caseTitle={case_.title} status={case_.status} onChanged={applyUpdate} />
              <CaseArchiveSwitch caseId={case_.id} caseTitle={case_.title} status={case_.status} onChanged={applyUpdate} />
            </div>
          </div>

          <dl className="grid grid-cols-1 gap-4 border-t pt-4 sm:grid-cols-3">
            <DetailField label="Cliente">
              {clientHref ? (
                <Link to={clientHref} className="text-primary underline-offset-4 hover:underline">{clientName}</Link>
              ) : clientName}
            </DetailField>
            {case_.courtName && <DetailField label="Vara / tribunal">{case_.courtName}</DetailField>}
            {case_.responsibleLawyerName && <DetailField label="Responsável">{case_.responsibleLawyerName}</DetailField>}
          </dl>

          <div className="flex flex-wrap items-center gap-2 border-t pt-4">
            {clientHref && (
              <Button variant="ghost" size="sm" asChild className="-ml-2">
                <Link to={clientHref}><ArrowLeft className="size-4" /> Voltar para {clientName}</Link>
              </Button>
            )}
            <div className="ml-auto flex flex-wrap gap-2">
              {canConvert && (
                <Button variant="ghost" size="sm" onClick={() => setShowConvertToLegalProcessModal(true)} title="Converter este caso em processo judicial">
                  <Scale className="size-4" /> Converter em processo
                </Button>
              )}
              <Button variant="outline" size="sm" asChild>
                <Link to={`/cases/${case_.id}/edit`}><Edit className="size-4" /> Editar</Link>
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <section aria-label="Prazos e tarefas" className="flex flex-col gap-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h2 className="text-lg font-semibold">Prazos e tarefas</h2>
          <TaskQuickCreateButton
            context={{
              clientId: case_.client?.id || case_.clientId || '',
              clientName,
              caseId: case_.id,
              caseTitle: case_.title,
              // A API exige um número quando há caso: o CNJ, ou o interno (que não aparece na tela)
              caseNumber: case_.courtNumber || case_.caseNumber,
              originType: 'from_case'
            } as TaskContext}
            onTaskCreated={() => setTaskRefreshTrigger(prev => prev + 1)}
            buttonLabel="Nova tarefa"
          />
        </div>
        <TaskListByEntity entityType="case" entityId={case_.id} showClient={false} showStats={false} showFilters={false} refreshTrigger={taskRefreshTrigger} />
      </section>

      <CaseDocuments
        caseId={case_.id}
        onDocumentUploaded={() => { /* no-op */ }}
        onDocumentDeleted={() => { /* no-op */ }}
      />

      <FinanceSummaryCard caseId={case_.id} hideWhenEmpty />

      <div className="flex flex-col gap-3">
        {hasMoreDetails && (
          <Folded title="Mais detalhes">
            <div className="flex flex-col gap-4">
              {case_.description && <p className="whitespace-pre-wrap text-sm leading-relaxed">{case_.description}</p>}
              <dl className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                {case_.area && <DetailField label="Área">{CaseAreaLabels[case_.area] ?? case_.area}</DetailField>}
                {case_.judgeName && <DetailField label="Juiz">{case_.judgeName}</DetailField>}
                {case_.startDate && <DetailField label="Início">{formatDateOnly(case_.startDate)}</DetailField>}
                {estimatedValue && <DetailField label="Valor estimado">{estimatedValue}</DetailField>}
                {!!case_.successFeePercentage && <DetailField label="Honorário de êxito">{case_.successFeePercentage}%</DetailField>}
              </dl>
              {case_.tags?.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {case_.tags.map((tag) => <Badge key={tag} variant="secondary">{tag}</Badge>)}
                </div>
              )}
            </div>
          </Folded>
        )}
        <Folded title="Honorários contingentes">
          <CaseContingencyFees caseId={case_.id} clientId={case_.clientId} caseTitle={case_.title} />
        </Folded>
      </div>

      <ConvertCaseToLegalProcessModal
        isOpen={showConvertToLegalProcessModal}
        onClose={() => setShowConvertToLegalProcessModal(false)}
        onSuccess={applyUpdate}
        caseData={{ id: case_.id, title: case_.title, description: case_.description }}
      />
    </div>
  )
}
